import React from "react";
import {
  FileText,
  AlertCircle,
  DollarSign,
  Scale,
  Shield,
  CheckCircle,
} from "lucide-react";

const TermsOfService = () => {
  return (
    <div className="relative bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 min-h-screen py-20 sm:py-24 lg:py-32 overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-orange-500/5 via-transparent to-orange-600/5" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-orange-500/10 rounded-full blur-[120px] animate-pulse" />
      </div>

      {/* Grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:32px_32px]" />

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-4xl px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-orange-500/10 border border-orange-500/20 px-4 py-2 mb-6">
            <FileText className="h-4 w-4 text-orange-400" />
            <span className="text-sm font-medium text-orange-300">
              Legal Agreement
            </span>
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl mb-6">
            Terms of{" "}
            <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">
              Service
            </span>
          </h1>
          <p className="text-lg text-slate-400">
            Last Updated:{" "}
            {new Date().toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </p>
        </div>

        {/* Content Card */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl shadow-2xl p-8 lg:p-12">
          <div className="prose prose-invert prose-slate max-w-none">
            {/* Introduction */}
            <div className="mb-8">
              <p className="text-slate-300 leading-relaxed">
                Please read these Terms of Service ("Terms") carefully before
                using the services provided by M2 Gas Services ("Company," "we,"
                "us," or "our"). By engaging our services, you agree to be bound
                by these Terms.
              </p>
            </div>

            {/* Section 1 */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold text-white m-0">
                  1. Services Provided
                </h2>
              </div>
              <div className="space-y-4 text-slate-300">
                <p className="leading-relaxed">
                  M2 Gas Services provides professional gas line installation,
                  repair, maintenance, leak detection, and related services
                  ("Services") for residential and commercial properties.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-white">
                    Our services include, but are not limited to:
                  </strong>
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Gas line installation and upgrades</li>
                  <li>Gas line repair and replacement</li>
                  <li>Gas leak detection and testing</li>
                  <li>Gas appliance hookups and connections</li>
                  <li>Routine maintenance and safety inspections</li>
                </ul>
              </div>
            </div>

            {/* Section 2 */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <DollarSign className="h-6 w-6 text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold text-white m-0">
                  2. Pricing and Payment
                </h2>
              </div>
              <div className="space-y-4 text-slate-300">
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    All prices are quoted in U.S. dollars and are subject to
                    change without notice
                  </li>
                  <li>
                    Estimates provided are based on information available at the
                    time and may be adjusted if additional work is required
                  </li>
                  <li>
                    Payment is due upon completion of services unless otherwise
                    agreed in writing
                  </li>
                  <li>We accept cash, check, and major credit cards</li>
                  <li>A deposit may be required for large projects</li>
                  <li>
                    Late payments may be subject to interest charges at the
                    maximum rate permitted by law
                  </li>
                  <li>
                    Customer is responsible for all costs of collection,
                    including reasonable attorney fees
                  </li>
                </ul>
              </div>
            </div>

            {/* Section 3 */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <AlertCircle className="h-6 w-6 text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold text-white m-0">
                  3. Scheduling and Cancellation
                </h2>
              </div>
              <div className="space-y-4 text-slate-300">
                <p className="leading-relaxed">
                  <strong className="text-white">Appointments:</strong>
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    Appointment times are estimates and may be affected by
                    factors beyond our control
                  </li>
                  <li>
                    We will make reasonable efforts to notify you of any
                    significant delays
                  </li>
                  <li>
                    Customer must provide safe and reasonable access to the work
                    area
                  </li>
                </ul>
                <p className="leading-relaxed mt-4">
                  <strong className="text-white">Cancellations:</strong>
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    Cancellations must be made at least 24 hours in advance
                  </li>
                  <li>
                    Cancellations with less than 24 hours notice may be subject
                    to a cancellation fee
                  </li>
                  <li>
                    We reserve the right to cancel or reschedule appointments
                    due to weather, emergencies, or other unforeseen
                    circumstances
                  </li>
                </ul>
              </div>
            </div>

            {/* Section 4 */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <Shield className="h-6 w-6 text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold text-white m-0">
                  4. Warranties and Guarantees
                </h2>
              </div>
              <div className="space-y-4 text-slate-300">
                <p className="leading-relaxed">
                  <strong className="text-white">Workmanship Warranty:</strong>{" "}
                  We guarantee our workmanship for a period of one (1) year from
                  the date of service completion. This warranty covers defects
                  in our installation or repair work under normal use and
                  service.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-white">
                    Equipment and Materials:
                  </strong>{" "}
                  Equipment and materials are warranted according to the
                  manufacturer's warranty terms.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-white">Warranty Exclusions:</strong>
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Damage caused by misuse, abuse, or neglect</li>
                  <li>Normal wear and tear</li>
                  <li>Acts of nature or circumstances beyond our control</li>
                  <li>Work performed by unauthorized third parties</li>
                  <li>Failure to follow recommended maintenance schedules</li>
                </ul>
              </div>
            </div>

            {/* Section 5 */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <Scale className="h-6 w-6 text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold text-white m-0">
                  5. Limitation of Liability
                </h2>
              </div>
              <div className="space-y-4 text-slate-300">
                <p className="leading-relaxed">
                  To the maximum extent permitted by law:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    Our total liability for any claim arising from our services
                    shall not exceed the amount paid for the specific service
                    that gave rise to the claim
                  </li>
                  <li>
                    We are not liable for indirect, incidental, consequential,
                    or special damages
                  </li>
                  <li>
                    We are not responsible for pre-existing conditions unless
                    specifically agreed in writing
                  </li>
                  <li>
                    Customer assumes all risk for property conditions not
                    disclosed to us prior to service
                  </li>
                  <li>
                    We are not liable for delays or failure to perform due to
                    circumstances beyond our reasonable control
                  </li>
                </ul>
                <p className="leading-relaxed mt-4">
                  <strong className="text-white">Insurance:</strong> M2 Gas
                  Services maintains liability insurance as required by North
                  Carolina law.
                </p>
              </div>
            </div>

            {/* Section 6 */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <AlertCircle className="h-6 w-6 text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold text-white m-0">
                  6. Customer Responsibilities
                </h2>
              </div>
              <div className="space-y-4 text-slate-300">
                <p className="leading-relaxed">Customer agrees to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    Provide accurate information about the property and service
                    needs
                  </li>
                  <li>Ensure safe access to work areas</li>
                  <li>
                    Secure pets and remove valuable or fragile items from work
                    areas
                  </li>
                  <li>
                    Obtain necessary permits and approvals (when customer is
                    responsible)
                  </li>
                  <li>
                    Notify us immediately of any concerns or issues with
                    completed work
                  </li>
                  <li>
                    Follow all safety recommendations and instructions provided
                    by our technicians
                  </li>
                  <li>
                    Maintain gas systems according to manufacturer and
                    professional recommendations
                  </li>
                </ul>
              </div>
            </div>

            {/* Section 7 */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <Shield className="h-6 w-6 text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold text-white m-0">
                  7. Safety and Compliance
                </h2>
              </div>
              <div className="space-y-4 text-slate-300">
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    All work is performed in accordance with applicable federal,
                    state, and local codes
                  </li>
                  <li>
                    Our technicians are licensed and trained in accordance with
                    North Carolina requirements
                  </li>
                  <li>
                    Safety is our top priority, and we reserve the right to
                    refuse or stop work if conditions are unsafe
                  </li>
                  <li>
                    Customer must evacuate premises immediately if advised by
                    our technicians due to safety concerns
                  </li>
                  <li>
                    Emergency gas leaks should be reported to 911 and your gas
                    utility company immediately
                  </li>
                </ul>
              </div>
            </div>

            {/* Section 8 */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <Scale className="h-6 w-6 text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold text-white m-0">
                  8. Dispute Resolution
                </h2>
              </div>
              <div className="space-y-4 text-slate-300">
                <p className="leading-relaxed">
                  <strong className="text-white">Informal Resolution:</strong>{" "}
                  In the event of any dispute, we encourage you to contact us
                  directly to seek an informal resolution.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-white">Governing Law:</strong> These
                  Terms are governed by the laws of the State of North Carolina,
                  without regard to conflict of law principles.
                </p>
                <p className="leading-relaxed">
                  <strong className="text-white">Venue:</strong> Any legal
                  action arising from these Terms or our services shall be
                  brought exclusively in the courts of Iredell County, North
                  Carolina.
                </p>
              </div>
            </div>

            {/* Section 9 */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <FileText className="h-6 w-6 text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold text-white m-0">
                  9. Changes to Terms
                </h2>
              </div>
              <div className="space-y-4 text-slate-300">
                <p className="leading-relaxed">
                  We reserve the right to modify these Terms at any time.
                  Updated Terms will be posted on our website with a revised
                  "Last Updated" date. Continued use of our services after
                  changes constitutes acceptance of the modified Terms.
                </p>
              </div>
            </div>

            {/* Section 10 */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <FileText className="h-6 w-6 text-orange-400" />
                </div>
                <h2 className="text-2xl font-bold text-white m-0">
                  10. Severability
                </h2>
              </div>
              <div className="space-y-4 text-slate-300">
                <p className="leading-relaxed">
                  If any provision of these Terms is found to be unenforceable
                  or invalid, that provision shall be limited or eliminated to
                  the minimum extent necessary, and the remaining provisions
                  shall remain in full force and effect.
                </p>
              </div>
            </div>

            {/* Contact Section */}
            <div className="mt-12 p-6 bg-orange-500/10 border border-orange-500/30 rounded-xl">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-orange-400" />
                Questions About These Terms?
              </h3>
              <p className="text-slate-300 mb-4">
                If you have any questions or concerns about these Terms of
                Service, please contact us:
              </p>
              <div className="space-y-2 text-slate-300">
                <p>
                  <strong className="text-white">M2 Gas Services</strong>
                </p>
                <p>133 Hightower Ct, Mooresville, NC 28117</p>
                <p>
                  <a
                    href="tel:9805406064"
                    className="text-orange-400 hover:text-orange-300"
                  >
                    Phone: (980) 540-6064
                  </a>
                </p>
                <p>
                  <a
                    href="mailto:support@m2gasservices.com"
                    className="text-orange-400 hover:text-orange-300"
                  >
                    Email: support@m2gasservices.com
                  </a>
                </p>
              </div>
            </div>

            {/* Agreement Notice */}
            <div className="mt-8 p-4 bg-slate-700/30 border border-slate-600/50 rounded-lg">
              <p className="text-sm text-slate-400 italic">
                By using our services, you acknowledge that you have read,
                understood, and agree to be bound by these Terms of Service.
              </p>
            </div>
          </div>
        </div>

        {/* Back to Home */}
        <div className="mt-8 text-center">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
          >
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
